import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { MarkShape } from '../../utils/calendarPalette';

export interface CalendarEvent {
  date: string;       // ISO date (YYYY-MM-DD)
  label: string;
  color?: string;     // dot color
  /**
   * Формата на маркера — вторият канал на значението до цвета. Шест цвята не се
   * различават надеждно на 4px при цветна слепота, затова часовете са кръгове, а
   * неучебните дни квадрати (виж `utils/calendarPalette`). По подразбиране кръг.
   */
  shape?: MarkShape;
  /**
   * Подробности, показвани САМО при кликване на деня — днес: пълното място на
   * урока (сграда · етаж · стая).
   *
   * 🔴 Нарочно НЕ влизат в `label`: той се показва и в решетката, и в
   * „Предстоящи", където редът е тесен и вече се реже с многоточие. Дълъг етикет
   * там изяжда точно това, което носи смисъл — името на детето и часа.
   */
  details?: string;
}

/**
 * Маркерът на събитие. ЕДНО място решава цвят и форма — маркери се рисуват на три
 * места (решетката, панелът на избрания ден, „Предстоящи") и ако едно от тях сложи
 * кръг вместо квадрат, каналът лъже точно там, където потребителят го проверява.
 */
function EventMark({ event, size }: { event: CalendarEvent; size: 'grid' | 'list' }) {
  const box = size === 'grid' ? 'w-1 h-1' : 'w-2 h-2';
  const shape = event.shape === 'square' ? 'rounded-[1px]' : 'rounded-full';
  return (
    <span
      className={`${box} ${shape} flex-shrink-0`}
      style={{ backgroundColor: event.color || '#3b82f6' }}
    />
  );
}

export interface CalendarWidgetProps {
  events?: CalendarEvent[];
  className?: string;
  onDayClick?: (date: string, events: CalendarEvent[]) => void;
}

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export default function CalendarWidget({ events = [], className = '', onDayClick }: CalendarWidgetProps) {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthName = currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const weekdays = WEEKDAY_KEYS.map(key => t(`admin.dashboard.calendar.weekdays.${key}`));

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = firstDay.getDay(); // 0=Sun

    const days: (number | null)[] = [];

    // Padding for days before month starts
    for (let i = 0; i < startPad; i++) {
      days.push(null);
    }

    // Actual days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(d);
    }

    return days;
  }, [year, month]);

  // Map events by date string for quick lookup
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const evt of events) {
      if (!map[evt.date]) map[evt.date] = [];
      map[evt.date].push(evt);
    }
    return map;
  }, [events]);

  // Stable for the component's lifetime — a fresh object per render made the
  // useMemo below recompute every render (exhaustive-deps).
  const today = useMemo(() => new Date(), []);
  const isToday = (day: number) =>
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const dateStr = (day: number) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDay(null);
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDay(null);
  };
  const goToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  const handleDayClick = (day: number) => {
    const ds = dateStr(day);
    const dayEvents = eventsByDate[ds] || [];

    if (selectedDay === ds) {
      // Toggle off
      setSelectedDay(null);
    } else {
      setSelectedDay(ds);
      onDayClick?.(ds, dayEvents);
    }
  };

  // Events for selected day
  const selectedDayEvents = selectedDay ? (eventsByDate[selectedDay] || []) : [];

  // Upcoming events (from today forward, max 5) — only shown when no day is selected
  const upcomingEvents = useMemo(() => {
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return events
      .filter(e => e.date >= todayStr)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 5);
  }, [events, today]);

  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  // Format a selected date nicely
  const formatSelectedDate = (ds: string) => {
    const d = new Date(ds + 'T00:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">
          {t('admin.dashboard.calendar.title')}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={prevMonth}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={goToday}
            className="px-2 py-0.5 text-xs text-primary-600 hover:bg-primary-50 rounded font-medium"
          >
            {t('admin.dashboard.calendar.today')}
          </button>
          <button
            onClick={nextMonth}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Month name */}
      <p className="text-sm text-gray-600 text-center mb-2 capitalize">{monthName}</p>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-0 mb-1">
        {weekdays.map((wd, i) => (
          <div key={i} className="text-center text-[10px] font-medium text-gray-400 uppercase py-1">
            {wd}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-0">
        {calendarDays.map((day, i) => {
          if (day === null) {
            return <div key={`pad-${i}`} className="h-8" />;
          }

          const ds = dateStr(day);
          const dayEvents = eventsByDate[ds] || [];
          const hasEvents = dayEvents.length > 0;
          const isTodayCell = isToday(day);
          const isSelected = selectedDay === ds;
          const isHovered = hoveredDay === day && hasEvents && !isSelected;

          return (
            <div
              key={day}
              className="relative flex flex-col items-center justify-center h-8"
              onMouseEnter={() => hasEvents && setHoveredDay(day)}
              onMouseLeave={() => setHoveredDay(null)}
            >
              <button
                type="button"
                onClick={() => handleDayClick(day)}
                className={`text-xs w-6 h-6 flex items-center justify-center rounded-full transition-colors ${
                  isSelected
                    ? 'bg-primary-100 text-primary-800 font-bold ring-2 ring-primary-400'
                    : isTodayCell
                      ? 'bg-primary-600 text-white font-bold'
                      : hasEvents
                        ? 'font-medium text-gray-900 hover:bg-primary-50 cursor-pointer'
                        : 'text-gray-600 hover:bg-gray-50 cursor-pointer'
                }`}
              >
                {day}
              </button>
              {hasEvents && (
                <div className="flex gap-0.5 absolute bottom-0">
                  {dayEvents.slice(0, 3).map((evt, j) => (
                    <EventMark key={j} event={evt} size="grid" />
                  ))}
                </div>
              )}

              {/* Tooltip on hover (only when not selected) */}
              {isHovered && (
                <div className="absolute z-20 bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] rounded px-2 py-1 whitespace-nowrap shadow-lg pointer-events-none">
                  {dayEvents.map((evt, j) => (
                    <div key={j}>{evt.label}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected day events detail */}
      {selectedDay && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-700 capitalize">
              {formatSelectedDate(selectedDay)}
            </p>
            <button
              onClick={() => setSelectedDay(null)}
              className="text-[10px] text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          {selectedDayEvents.length > 0 ? (
            <div className="space-y-1.5">
              {selectedDayEvents.map((evt, i) => (
                <div key={i} className="flex items-start gap-2 text-xs p-1.5 rounded-md bg-gray-50">
                  <span className="mt-1"><EventMark event={evt} size="list" /></span>
                  <span className="text-gray-700">
                    {evt.label}
                    {evt.details && (
                      <span className="block text-[11px] text-gray-500">{evt.details}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">{t('admin.dashboard.calendar.noEvents', 'Няма събития')}</p>
          )}
        </div>
      )}

      {/* Upcoming events list — shown when no day is selected */}
      {!selectedDay && upcomingEvents.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-500 mb-2 uppercase">
            {t('admin.dashboard.calendar.upcoming')}
          </p>
          <div className="space-y-1.5">
            {upcomingEvents.map((evt, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <EventMark event={evt} size="list" />
                <span className="text-gray-500 flex-shrink-0">
                  {new Date(evt.date + 'T00:00:00').toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })}
                </span>
                <span className="text-gray-700 truncate">{evt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
